import React from 'react';
import {
  Button,
  ButtonType,
  Drawer,
  Row
} from 'syngenta-digital-cropwise-react-ui-kit';

export interface FilterDrawerShellProps {
  /** Whether the drawer is open. */
  visible: boolean;
  /** Heading shown at the top of the drawer body. */
  title: string;
  /** `data-testid` + drawer className hook (owner supplies its own CSS class). */
  testId?: string;
  className?: string;
  /** Disables the primary "Apply" button (e.g. pristine / invalid form). */
  applyDisabled: boolean;
  /** Disables the "Clear All" button (e.g. already at empty defaults). */
  clearAllDisabled: boolean;
  cancelLabel?: string;
  applyLabel?: string;
  clearAllLabel?: string;
  /** Form submit handler (typically `formik.handleSubmit`). */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  /** "Cancel": owner resets its form to the applied values and closes. */
  onCancel: () => void;
  /** "Apply": owner emits telemetry (if any), submits, and closes. */
  onApply: () => void;
  /** "Clear All": owner touches all fields and resets to empty defaults. */
  onClearAll: () => void;
  /** Operation-specific filter field bodies. */
  children: React.ReactNode;
}

/**
 * Presentational chrome shared by every records filter drawer (Applications,
 * Work Orders, Invoices): the `<form>` + right-placed `Drawer`, the
 * Cancel/Apply footer, and the "Filter" header with a "Clear All" button.
 *
 * It holds no Formik state and no domain logic — each owner keeps its own
 * Formik instance (value shapes differ) and passes the derived enabled/disabled
 * flags, the button handlers, and the filter field bodies as `children`.
 */
const FilterDrawerShell: React.FC<FilterDrawerShellProps> = ({
  visible,
  title,
  testId,
  className,
  applyDisabled,
  clearAllDisabled,
  cancelLabel = 'Cancel',
  applyLabel = 'Apply',
  clearAllLabel = 'Clear All',
  onSubmit,
  onCancel,
  onApply,
  onClearAll,
  children
}) => (
  <form onSubmit={onSubmit}>
    <Drawer
      placement="right"
      open={visible}
      closable={false}
      className={className}
      data-testid={testId}
      footer={
        <Row justify="space-between">
          <Button
            type={ButtonType.outline}
            className="px-3"
            onClick={onCancel}
            size="middle"
          >
            {cancelLabel}
          </Button>
          <Button
            type={ButtonType.primary}
            className="px-3"
            disabled={applyDisabled}
            onClick={onApply}
            size="middle"
          >
            {applyLabel}
          </Button>
        </Row>
      }
    >
      <div className="flex pb-6 px-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        <Button
          type={ButtonType.outline}
          size="small"
          disabled={clearAllDisabled}
          onClick={onClearAll}
          className="ml-auto"
        >
          {clearAllLabel}
        </Button>
      </div>
      {children}
    </Drawer>
  </form>
);

export default FilterDrawerShell;
